import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FileUpload } from 'primeng/fileupload';
import { FileUploadService } from '../../../_services/file-upload.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Storage, ref, listAll, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { catchError, of } from 'rxjs'; // Removed firstValueFrom if not used elsewhere, kept catchError/of

interface CachedModels {
  timestamp: number;
  models: any[];
}

// Add new interface for upload status
interface UploadStatus {
  state: 'idle' | 'uploading' | 'success' | 'error';
  message?: string;
  error?: any;
}

@Component({
  selector: 'app-model-selector',
  templateUrl: './model-selector.component.html',
  styleUrls: ['./model-selector.component.scss']
})
export class ModelSelectorComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() models: any[];
  @Input() selectedModel: any;
  @Input() modelFile: any;
  @Input() clientId: string;
  @Input() projectId: string;
  @Output() selectedModelChange = new EventEmitter<any>();
  @Output() modelFileChange = new EventEmitter<any>();
  @Output() modelSelected = new EventEmitter<any>();

  uploadModalVisible: boolean = false;
  activeStep: number = 0;
  
  // Preview states
  modelPreviewUrl: string | null = null;
  thumbnailPreviewUrl: string | null = null;
  
  // Upload states
  uploadedModel: File | null = null;
  uploadedThumbnail: File | null = null;

  customModels: any[] = [];
  selectedModelUrl: string;

  modelName: string = '';
  uploadProgress: number = 0;
  isUploading: boolean = false;

  private readonly CACHE_KEY = 'custom-models-cache';
  private readonly CACHE_DURATION = 1000 * 60 * 60; // 1 hour in milliseconds

  uploadStatus: UploadStatus = { state: 'idle' };

  showDeleteConfirmation: boolean = false;
  modelToDelete: any = null;
  deleteConfirmationMessage: string = '';

  autoScaleEnabled: boolean = false;
  modelSize = {
    x: 1,
    y: 1,
    z: 1
  };

  constructor(
    private fileUploadService: FileUploadService,
    private messageService: MessageService,
    private storage: Storage,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {

    await this.loadCustomModelsWithCache();
    // console.log('[ModelSelectorComponent] ngOnInit - Initial models:', this.models); // Removed log
    // console.log('[ModelSelectorComponent] ngOnInit - Initial selectedModel:', this.selectedModel); // Removed log
  }

  ngOnChanges(changes: SimpleChanges) {
    // console.log('[ModelSelectorComponent] ngOnChanges - Changes detected:', changes); // Removed log
    if (changes['selectedModel'] && changes['selectedModel'].currentValue) {
      const model = changes['selectedModel'].currentValue;
      this.autoScaleEnabled = model.autoScale || false;

      // If model doesn't have modelSize and is not a custom model, try to find it in models array
      if (!model.modelSize && !model.isCustom && this.models) {
        const defaultModel = this.models.find(m => m.id === model.id);
        this.modelSize = defaultModel?.modelSize || { x: 1, y: 1, z: 1 };
      } else {
        this.modelSize = model.modelSize || { x: 1, y: 1, z: 1 };
      }
    }
  }

  private getCachedModels(): CachedModels | null {
    const cached = localStorage.getItem(this.CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
    return null;
  }

  private setCachedModels(models: any[]) {
    const cache: CachedModels = {
      timestamp: Date.now(),
      models: models
    };
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
  }

  private isCacheValid(cache: CachedModels): boolean {
    return Date.now() - cache.timestamp < this.CACHE_DURATION;
  }

  async loadCustomModelsWithCache() {
    try {
      // Always get the current list from Firebase first
      console.log("loading custom models from " + `clients/${this.clientId}/custom-models`)
      const storageRef = ref(this.storage, `clients/${this.clientId}/custom-models`);
      const list = await listAll(storageRef);
      console.log("Found total of " + list.items.length)
      const existingFolders = list.prefixes.map(prefix => prefix.name);

      const cached = this.getCachedModels();
      const customModels: any[] = [];

      // If we have valid cache, use it for existing models only
      if (cached && this.isCacheValid(cached)) {
        cached.models
          .filter(model => existingFolders.includes(model.id))
          .forEach(model => {
            if (!this.models?.find(m => m.id === model.id)) {
              customModels.push(model);
            }
          });
      }

      // For any new or uncached models, fetch their data
      for (const folder of list.prefixes) {
        const folderName = folder.name;
        if (!customModels.find(m => m.id === folderName)) {
          try {
            const modelRef = ref(this.storage, `clients/${this.clientId}/custom-models/${folderName}/${folderName}.glb`);
            const modelUrl = await getDownloadURL(modelRef);
            const thumbnailRef = ref(this.storage, `clients/${this.clientId}/custom-models/${folderName}/${folderName}-thumbnail.png`);
            const thumbnailUrl = await getDownloadURL(thumbnailRef);

            const customModel = {
              id: folderName,
              url: modelUrl,
              image: thumbnailUrl,
              isCustom: true,
              name: folderName,
              timeCreated: Date.now().toString()
            };

            customModels.push(customModel);
          } catch (error) {
            console.error(`Error loading model ${folderName}:`, error);
          }
        }
      }

      // Update cache with current models
      this.setCachedModels(customModels);

      // Update models array
      if (!this.models) {
        this.models = [];
      }

      // Remove any deleted custom models
      this.models = this.models.filter(m => !m.isCustom || existingFolders.includes(m.id));

      // Add new models
      customModels.forEach(model => {
        const existingIndex = this.models.findIndex(m => m.id === model.id);
        if (existingIndex >= 0) {
          this.models[existingIndex] = model;
        } else {
          this.models.push(model);
        }
      });

    } catch (error) {
      console.error('Error loading custom models:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load custom models'
      });
    }
  }

  ngAfterViewInit(): void {
    this.selectedModelUrl = "assets/models/"+ (this.selectedModel?.id ?? 14) + ".glb"
  }

  showUploadModal() {
    console.log('showUploadModal called');
    this.uploadModalVisible = true;
    this.activeStep = 0;
    this.resetUploadStates();
  }

  resetUploadStates() {
    this.modelPreviewUrl = null;
    this.thumbnailPreviewUrl = null;
    this.uploadedModel = null;
    this.uploadedThumbnail = null;
  }

  onModelUpload(event: any) {
    const file = event.files[0];
    if (this.isValidModelFile(file)) {
      this.uploadedModel = file;
      this.modelPreviewUrl = URL.createObjectURL(file);
    }
  }

  onThumbnailUpload(event: any) {
    const file = event.files[0];
    if (this.isValidImageFile(file)) {
      this.uploadedThumbnail = file;
      this.thumbnailPreviewUrl = URL.createObjectURL(file);
    }
  }

  isValidModelFile(file: File): boolean {
    const validTypes = ['.glb', '.gltf', '.fbx'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    return validTypes.includes(fileExtension);
  }

  isValidImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  nextStep() {
    if (this.activeStep < 2) {
      this.activeStep++;
    }
  }

  prevStep() {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  }

  validateModelName(): boolean {
    return this.modelName.trim().length > 0 && /^[a-zA-Z0-9-_]+$/.test(this.modelName);
  }

  async finishUpload() {
    if (!this.uploadedModel || !this.uploadedThumbnail || !this.validateModelName()) {
      return;
    }

    this.uploadStatus = { state: 'uploading' };
    this.isUploading = true;
    this.uploadProgress = 0;

    try {
      // Upload model file
      const modelPath = `clients/${this.clientId}/custom-models/${this.modelName}/${this.modelName}.glb`;
      const modelUrl = await this.fileUploadService.uploadFile(this.uploadedModel, modelPath);
      this.uploadProgress = 50;

      // Upload thumbnail
      const thumbnailPath = `clients/${this.clientId}/custom-models/${this.modelName}/${this.modelName}-thumbnail.png`;
      const thumbnailUrl = await this.fileUploadService.uploadFile(this.uploadedThumbnail, thumbnailPath);
      this.uploadProgress = 100;

      const newModel = {
        id: this.modelName,
        url: modelUrl,
        image: thumbnailUrl,
        isCustom: true,
        name: this.modelName,
        timeCreated: Date.now().toString()
      };

      if (!this.models) {
        this.models = [];
      }
      this.models.push(newModel);
      this.selectModel(newModel);

      // Update status to success
      this.uploadStatus = { 
        state: 'success',
        message: 'Model uploaded successfully!' 
      };
      this.isUploading = false;

      // After successful upload, invalidate cache
      const cached = this.getCachedModels();
      if (cached) {
        cached.timestamp = 0;
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(cached));
      }

      await this.loadCustomModelsWithCache();

    } catch (error) {
      console.error('Upload error:', error);
      this.uploadStatus = { 
        state: 'error',
        message: 'Failed to upload model',
        error: error 
      };
      this.isUploading = false;
    }
  }

  resetUpload() {
    this.uploadStatus = { state: 'idle' };
    this.isUploading = false;
    this.uploadProgress = 0;
  }

  closeUploadModal() {
    this.uploadModalVisible = false;
    this.activeStep = 0;
    this.modelPreviewUrl = null;
    this.thumbnailPreviewUrl = null;
    this.uploadedModel = null;
    this.uploadedThumbnail = null;
    this.modelName = '';
    this.resetUpload();
  }

  selectModel(model: any) {
    this.selectedModel = model;
    // Initialize auto-scale settings when selecting a new model
    this.autoScaleEnabled = model?.autoScale || false;
    this.modelSize = model?.modelSize || { x: 1, y: 1, z: 1 };
    this.selectedModelChange.emit(this.selectedModel);
    if (model.isCustom) {
      this.selectedModelUrl = model.url;
    } else {
      this.selectedModelUrl = "assets/models/"+ (this.selectedModel?.id ?? 14) + ".glb";
    }
  }

  onFileDropped(files: any) {
    this.prepareFilesList(files);
  }

  fileBrowseHandler(files: any) {
    this.prepareFilesList(files);
  }
  getModelImagePath(modelId: string): string {
    return 'assets/models/' + modelId + '.png';
  }
  prepareFilesList(files: Array<any>) {
    if (files.length > 0) {
      const file = files[0];
      if (this.isValidFile(file)) {
        this.modelFile = {
          name: file.name,
          url: URL.createObjectURL(file),
          size: file.size,
          isUploaded: false,
          type: this.getFileType(file),
          file: file
        };
        this.modelFileChange.emit(this.modelFile);
        this.selectedModelUrl = "assets/models/"+ (this.modelFile?.id ?? 14) +".glb"
      }
    }
  }

  isValidFile(file: any): boolean {
    const validTypes = ['.glb', '.gltf', '.fbx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return validTypes.includes(fileExtension);
  }

  getFileType(file: any): string {
    return file.name.split('.').pop().toLowerCase();
  }

  confirmDelete(event: Event, model: any) {
    event.stopPropagation();
    this.modelToDelete = model;
    this.deleteConfirmationMessage = `Are you sure you want to delete "${model.name}"? This action cannot be undone.`;
    this.showDeleteConfirmation = true;
  }

  async onDeleteConfirm() {
    if (this.modelToDelete) {
      await this.deleteModel(this.modelToDelete);
      this.modelToDelete = null;
    }
  }

  onDeleteCancel() {
    this.modelToDelete = null;
  }

  async deleteModel(model: any) {
    try {
      const deletePromises: Promise<any>[] = [];

      // Queue up delete operations for both files
      const modelRef = ref(this.storage, `clients/${this.clientId}/custom-models/${model.id}/${model.id}.glb`);
      deletePromises.push(
        deleteObject(modelRef).catch(error => {
          if (error.code !== 'storage/object-not-found') {
            throw error; // Re-throw only if it's not 'object-not-found'
          }
          // If it is 'object-not-found', do nothing (effectively returning Promise<void>)
        })
      );

      const thumbnailRef = ref(this.storage, `clients/${this.clientId}/custom-models/${model.id}/${model.id}-thumbnail.png`);
      deletePromises.push(
        deleteObject(thumbnailRef).catch(error => {
          if (error.code !== 'storage/object-not-found') {
            throw error; // Re-throw only if it's not 'object-not-found'
          }
          // If it is 'object-not-found', do nothing (effectively returning Promise<void>)
        })
      );

      // Wait for all deletions to complete
      await Promise.all(deletePromises);

      // If the deleted model was selected, reset selection to default first
      if (this.selectedModel?.id === model.id) {
        const defaultModel = this.models.find(m => !m.isCustom) || this.models[0];
        this.selectModel(defaultModel);
      }

      // Create a new array reference to trigger change detection
      this.models = [...this.models.filter(m => m.id !== model.id)];

      // Clear cache
      localStorage.removeItem(this.CACHE_KEY);

      // Force change detection
      this.cdr.detectChanges();

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Model deleted successfully'
      });

    } catch (error) {
      console.error('Error deleting model:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete model'
      });
    }
  }

  // Add this method to check if model is custom
  isCustomModel(): boolean {
    return this.selectedModel?.isCustom || false;
  }

  toggleAutoScale() {
    if (this.selectedModel) {
      this.selectedModel.autoScale = this.autoScaleEnabled;
      
      if (this.autoScaleEnabled) {
        this.selectedModel.modelSize = { ...this.modelSize };
      } else {
        // Reset model size when disabling auto-scale
        this.modelSize = { x: 1, y: 1, z: 1 };
        this.selectedModel.modelSize = { x: 1, y: 1, z: 1 };
      }
      
      // Force change detection
      this.cdr.detectChanges();
      this.selectedModelChange.emit(this.selectedModel);
    }
  }

  updateModelSize() {
    if (this.selectedModel && this.autoScaleEnabled) {
      this.selectedModel.modelSize = { ...this.modelSize };
      this.selectedModelChange.emit(this.selectedModel);
    }
  }
}